// NUTRITION — the pillar's RESULT SHAPE, written by the BL through
// `writeNutritionAttachItem` and read by the FE through
// `readNutritionAttachItem`.
//
// ─── THE UNITS DISAGREE, AND THAT IS THE VENDOR'S DOING ─────────────────────
//
// A food serving's vitamin A, vitamin C, calcium and iron are METRIC AMOUNTS.
// A recipe's four are PERCENTAGES OF DAILY INTAKE. `28` and `0` sit in the same
// slot meaning micrograms on one card and per cent on the next, so the two
// never share a key: a food serving writes `micros`, a recipe serving writes
// `daily intake`. Nothing is converted.
//
// The serving's own household measure opens its group on its own line:
// `1/2 small (yield after cooking, bone removed)` is the widest captured
// measure and it carries commas and parentheses.
//
// ─── CENSUS (D28), from `fatsecret/examples.json` ───────────────────────────
//
//   Food row       foodId·name·kind·nutritionSummary·url 3/3
//     (`fatsecret.foodSearch` `chicken breast`). `brandName` is populated 1/3 —
//     null on both Generic rows, `Kirkland Signature` on the Brand row.
//   Food record    foodId·name·kind·url·servings 1/1 (`fatsecret.foodDetail`
//     food `1641`: three servings, each with description·metricAmount·
//     metricUnit and a full nutrient bag). `transFat` and `addedSugars` are
//     null across every captured serving, so every nutrient is optional and a
//     null one prints no part at all.
//   Recipe row     recipeId·name·description·imageUrl·url·perServing 3/3
//     (`fatsecret.recipeSearch` `peanut butter cookies`). `ingredientLines` is
//     null on all three — the search index does not carry them.
//   Recipe record  1/1 (`fatsecret.recipeDetail` recipe `118858365`: 4
//     directions, 10 categories, per-serving carrying both the metric block and
//     the four daily percentages).
// ════════════════════════════════════════════════════════════════════════════

import type { AttachFieldLine, AttachItemBlock } from '../grammar.js';
import { attachFieldValue } from '../parse-attach-document.js';
import {
  attachFieldGroups,
  attachGroupValue,
  attachNumberOf,
  attachNumberText,
  attachPartNumber,
  attachSubRecordOf,
  attachSubRecordText,
  attachTextOf,
  type AttachPartDraft,
  type AttachSubRecord,
} from './attach-value-codec.js';

/** NU's closed field vocabulary — both records, one spelling each. */
export enum NutritionAttachField {
  /** What the food or the dish is called. */
  Name = 'name',
  /** Which of the pillar's two records this is. */
  Kind = 'kind',
  /** The vendor's own filing of a food — `Generic` or `Brand`, verbatim. */
  FoodKind = 'food kind',
  /** Whose product it is. Food only, and only where the vendor named one. */
  Brand = 'brand',
  /** The search index's unit-bound one-liner for a listed food, verbatim. */
  Nutrition = 'nutrition',
  /** What the dish is, in the author's words. Free text, its own line. */
  Description = 'description',
  /** How many servings the whole recipe yields. */
  Yields = 'yields',
  /** Hands-on minutes before it goes in. */
  PrepMinutes = 'prep minutes',
  /** Minutes in the oven or on the heat. */
  CookMinutes = 'cook minutes',
  /** What one portion weighs. */
  GramsPerPortion = 'grams per portion',
  /** How the vendor files the dish — course, cuisine, occasion. REPEATED. */
  Category = 'category',
  /** The vendor's own recipe type. REPEATED. */
  Type = 'type',
  /** One measured ingredient, as the author wrote it. REPEATED. */
  Ingredient = 'ingredient',
  /** One direction, in the order it is performed. REPEATED. */
  Step = 'step',
  /** A photograph of the food or the finished dish. REPEATED, in wire order —
   *  the first is the thumbnail. */
  Photo = 'photo',
  /** One serving the thing is measured in. REPEATED, and it opens a GROUP: the
   *  `metric amount`, `metric unit`, `macros` and `daily intake` lines under it
   *  are its own. */
  Serving = 'serving',
  /** The serving's weight or volume, for comparing two different measures. */
  MetricAmount = 'metric amount',
  /** The unit that weight is in — `g`, `oz`, `ml`. */
  MetricUnit = 'metric unit',
  /** Everything the serving above contains, in the units both endpoints agree. */
  Macros = 'macros',
  /** The four the RECIPE index reports as a percentage of daily intake. */
  DailyIntake = 'daily intake',
  /** The four micronutrients a FOOD serving carries, as metric amounts. */
  Micros = 'micros',
  /** The durable link the writer attached this row under — the vendor's own
   *  page for this food or dish. */
  Link = 'link',
}

/** The parts of the nutrient sub-records; units are the endpoint's own. */
export enum NutritionAttachPart {
  Calories = 'calories',
  Carbs = 'carbs',
  Protein = 'protein',
  Fat = 'fat',
  SaturatedFat = 'saturated fat',
  TransFat = 'trans fat',
  PolyunsaturatedFat = 'polyunsaturated fat',
  MonounsaturatedFat = 'monounsaturated fat',
  Fiber = 'fiber',
  Sugar = 'sugar',
  AddedSugars = 'added sugars',
  Cholesterol = 'cholesterol',
  Sodium = 'sodium',
  Potassium = 'potassium',
  VitaminA = 'vitamin a',
  VitaminC = 'vitamin c',
  Calcium = 'calcium',
  Iron = 'iron',
}

/** The vendor's own two indexes, read back by exact match. */
export enum NutritionAttachKind {
  Food = 'food',
  Recipe = 'recipe',
}

const NUTRITION_KINDS: ReadonlySet<string> = new Set<string>(
  Object.values(NutritionAttachKind),
);

// ─── the typed shape ────────────────────────────────────────────────────────

/** Grams except where named otherwise; calories are kcal and the three
 *  minerals are milligrams. */
export interface NutritionAttachMacros {
  readonly calories: number | null;
  readonly carbohydrate: number | null;
  readonly protein: number | null;
  readonly fat: number | null;
  readonly saturatedFat: number | null;
  readonly transFat: number | null;
  readonly polyunsaturatedFat: number | null;
  readonly monounsaturatedFat: number | null;
  readonly fiber: number | null;
  readonly sugar: number | null;
  readonly addedSugars: number | null;
  /** Milligrams. */
  readonly cholesterol: number | null;
  readonly sodium: number | null;
  readonly potassium: number | null;
}

/** The four whose unit depends on which endpoint answered. */
export interface NutritionAttachMicros {
  readonly vitaminA: number | null;
  readonly vitaminC: number | null;
  readonly calcium: number | null;
  readonly iron: number | null;
}

/** One serving, and everything measured against it. */
export interface NutritionAttachServing {
  /** The household measure, verbatim. */
  readonly measure: string;
  readonly metricAmount: number | null;
  readonly metricUnit: string | null;
  readonly macros: NutritionAttachMacros;
  /** Metric amounts. FOOD servings only. */
  readonly micros: NutritionAttachMicros | null;
  /** Percentages of daily intake. RECIPE servings only. */
  readonly dailyIntake: NutritionAttachMicros | null;
}

/** One nutrition row, typed. The object a widget renders from. */
export interface NutritionAttachResult {
  readonly name: string;
  /** Which index answered. A card's whole micronutrient basis hangs off it. */
  readonly kind: NutritionAttachKind;
  /** The vendor's own food filing, verbatim. */
  readonly foodKind: string | null;
  readonly brand: string | null;
  /** The search index's own unit-bound one-liner. */
  readonly nutritionSummary: string | null;
  readonly description: string | null;
  readonly numberOfServings: number | null;
  readonly preparationTimeMinutes: number | null;
  readonly cookingTimeMinutes: number | null;
  readonly gramsPerPortion: number | null;
  readonly types: readonly string[];
  readonly categories: readonly string[];
  readonly ingredients: readonly string[];
  readonly directions: readonly string[];
  readonly photoUrls: readonly string[];
  readonly servings: readonly NutritionAttachServing[];
  readonly linkUrl: string | null;
}

// ─── the headline ───────────────────────────────────────────────────────────

/** The human read of one row — DISPLAY ONLY. */
export function nutritionAttachHeadline(input: {
  result: NutritionAttachResult;
}): string {
  return input.result.name;
}

// ─── writing ────────────────────────────────────────────────────────────────

function textLine(input: {
  key: NutritionAttachField;
  value: string | null;
}): AttachFieldLine[] {
  const value = attachTextOf({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberLine(input: {
  key: NutritionAttachField;
  value: number | null;
}): AttachFieldLine[] {
  const value = input.value === null ? null : attachNumberText({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function textLines(input: {
  key: NutritionAttachField;
  values: readonly string[];
}): AttachFieldLine[] {
  return input.values.flatMap((value) => textLine({ key: input.key, value }));
}

function part(input: {
  key: NutritionAttachPart;
  value: number | null;
}): AttachPartDraft {
  return {
    key: input.key,
    value: input.value === null ? null : attachNumberText({ value: input.value }),
  };
}

function macrosText(input: { macros: NutritionAttachMacros }): string | null {
  const macros = input.macros;
  return attachSubRecordText({
    parts: [
      part({ key: NutritionAttachPart.Calories, value: macros.calories }),
      part({ key: NutritionAttachPart.Carbs, value: macros.carbohydrate }),
      part({ key: NutritionAttachPart.Protein, value: macros.protein }),
      part({ key: NutritionAttachPart.Fat, value: macros.fat }),
      part({ key: NutritionAttachPart.SaturatedFat, value: macros.saturatedFat }),
      part({ key: NutritionAttachPart.TransFat, value: macros.transFat }),
      part({
        key: NutritionAttachPart.PolyunsaturatedFat,
        value: macros.polyunsaturatedFat,
      }),
      part({
        key: NutritionAttachPart.MonounsaturatedFat,
        value: macros.monounsaturatedFat,
      }),
      part({ key: NutritionAttachPart.Fiber, value: macros.fiber }),
      part({ key: NutritionAttachPart.Sugar, value: macros.sugar }),
      part({ key: NutritionAttachPart.AddedSugars, value: macros.addedSugars }),
      part({ key: NutritionAttachPart.Cholesterol, value: macros.cholesterol }),
      part({ key: NutritionAttachPart.Sodium, value: macros.sodium }),
      part({ key: NutritionAttachPart.Potassium, value: macros.potassium }),
    ],
  });
}

function microsText(input: { micros: NutritionAttachMicros }): string | null {
  const micros = input.micros;
  return attachSubRecordText({
    parts: [
      part({ key: NutritionAttachPart.VitaminA, value: micros.vitaminA }),
      part({ key: NutritionAttachPart.VitaminC, value: micros.vitaminC }),
      part({ key: NutritionAttachPart.Calcium, value: micros.calcium }),
      part({ key: NutritionAttachPart.Iron, value: micros.iron }),
    ],
  });
}

function servingLines(input: {
  serving: NutritionAttachServing;
}): AttachFieldLine[] {
  const serving = input.serving;
  return [
    ...textLine({ key: NutritionAttachField.Serving, value: serving.measure }),
    ...numberLine({
      key: NutritionAttachField.MetricAmount,
      value: serving.metricAmount,
    }),
    ...textLine({
      key: NutritionAttachField.MetricUnit,
      value: serving.metricUnit,
    }),
    ...textLine({
      key: NutritionAttachField.Macros,
      value: macrosText({ macros: serving.macros }),
    }),
    ...textLine({
      key: NutritionAttachField.Micros,
      value: serving.micros === null ? null : microsText({ micros: serving.micros }),
    }),
    ...textLine({
      key: NutritionAttachField.DailyIntake,
      value:
        serving.dailyIntake === null
          ? null
          : microsText({ micros: serving.dailyIntake }),
    }),
  ];
}

/** One typed nutrition row as a block item. */
export function writeNutritionAttachItem(input: {
  result: NutritionAttachResult;
}): AttachItemBlock {
  const result = input.result;
  return {
    headline: nutritionAttachHeadline({ result }),
    fields: [
      ...textLine({ key: NutritionAttachField.Name, value: result.name }),
      ...textLine({ key: NutritionAttachField.Kind, value: result.kind }),
      ...textLine({ key: NutritionAttachField.FoodKind, value: result.foodKind }),
      ...textLine({ key: NutritionAttachField.Brand, value: result.brand }),
      ...textLine({
        key: NutritionAttachField.Nutrition,
        value: result.nutritionSummary,
      }),
      ...textLine({
        key: NutritionAttachField.Description,
        value: result.description,
      }),
      ...numberLine({
        key: NutritionAttachField.Yields,
        value: result.numberOfServings,
      }),
      ...numberLine({
        key: NutritionAttachField.PrepMinutes,
        value: result.preparationTimeMinutes,
      }),
      ...numberLine({
        key: NutritionAttachField.CookMinutes,
        value: result.cookingTimeMinutes,
      }),
      ...numberLine({
        key: NutritionAttachField.GramsPerPortion,
        value: result.gramsPerPortion,
      }),
      ...textLines({ key: NutritionAttachField.Type, values: result.types }),
      ...textLines({
        key: NutritionAttachField.Category,
        values: result.categories,
      }),
      ...textLines({
        key: NutritionAttachField.Ingredient,
        values: result.ingredients,
      }),
      ...textLines({ key: NutritionAttachField.Step, values: result.directions }),
      ...textLines({ key: NutritionAttachField.Photo, values: result.photoUrls }),
      ...textLine({ key: NutritionAttachField.Link, value: result.linkUrl }),
      ...result.servings.flatMap((serving) => servingLines({ serving })),
    ],
  };
}

// ─── reading ────────────────────────────────────────────────────────────────

function fieldText(input: {
  item: AttachItemBlock;
  key: NutritionAttachField;
}): string | null {
  return attachTextOf({ value: attachFieldValue({ item: input.item, key: input.key }) });
}

function fieldNumber(input: {
  item: AttachItemBlock;
  key: NutritionAttachField;
}): number | null {
  return attachNumberOf({ value: fieldText(input) });
}

function fieldValues(input: {
  item: AttachItemBlock;
  key: NutritionAttachField;
}): string[] {
  return input.item.fields
    .filter((field) => field.key === input.key)
    .flatMap((field) => {
      const value = attachTextOf({ value: field.value });
      return value === null ? [] : [value];
    });
}

/** The record a `kind:` line names, or null where it named one outside the
 *  pillar's own two. */
export function nutritionAttachKindOf(input: {
  value: string | null;
}): NutritionAttachKind | null {
  const value = attachTextOf({ value: input.value });
  return value !== null && NUTRITION_KINDS.has(value)
    ? (value as NutritionAttachKind)
    : null;
}

function macrosOf(input: { record: AttachSubRecord }): NutritionAttachMacros {
  const record = input.record;
  const figure = (key: NutritionAttachPart): number | null =>
    attachPartNumber({ record, key });
  return {
    calories: figure(NutritionAttachPart.Calories),
    carbohydrate: figure(NutritionAttachPart.Carbs),
    protein: figure(NutritionAttachPart.Protein),
    fat: figure(NutritionAttachPart.Fat),
    saturatedFat: figure(NutritionAttachPart.SaturatedFat),
    transFat: figure(NutritionAttachPart.TransFat),
    polyunsaturatedFat: figure(NutritionAttachPart.PolyunsaturatedFat),
    monounsaturatedFat: figure(NutritionAttachPart.MonounsaturatedFat),
    fiber: figure(NutritionAttachPart.Fiber),
    sugar: figure(NutritionAttachPart.Sugar),
    addedSugars: figure(NutritionAttachPart.AddedSugars),
    cholesterol: figure(NutritionAttachPart.Cholesterol),
    sodium: figure(NutritionAttachPart.Sodium),
    potassium: figure(NutritionAttachPart.Potassium),
  };
}

/** The four, or null where the line was absent. */
function microsOf(input: { value: string | null }): NutritionAttachMicros | null {
  if (attachTextOf({ value: input.value }) === null) return null;
  const record = attachSubRecordOf({ value: input.value });
  return {
    vitaminA: attachPartNumber({ record, key: NutritionAttachPart.VitaminA }),
    vitaminC: attachPartNumber({ record, key: NutritionAttachPart.VitaminC }),
    calcium: attachPartNumber({ record, key: NutritionAttachPart.Calcium }),
    iron: attachPartNumber({ record, key: NutritionAttachPart.Iron }),
  };
}

function servingsOf(input: { item: AttachItemBlock }): NutritionAttachServing[] {
  return attachFieldGroups({
    item: input.item,
    openKey: NutritionAttachField.Serving,
    memberKeys: [
      NutritionAttachField.MetricAmount,
      NutritionAttachField.MetricUnit,
      NutritionAttachField.Macros,
      NutritionAttachField.Micros,
      NutritionAttachField.DailyIntake,
    ],
  }).flatMap((group) => {
    const measure = attachTextOf({ value: group.open });
    if (measure === null) return [];
    return [
      {
        measure,
        metricAmount: attachNumberOf({
          value: attachGroupValue({
            group,
            key: NutritionAttachField.MetricAmount,
          }),
        }),
        metricUnit: attachGroupValue({
          group,
          key: NutritionAttachField.MetricUnit,
        }),
        macros: macrosOf({
          record: attachSubRecordOf({
            value: attachGroupValue({ group, key: NutritionAttachField.Macros }),
          }),
        }),
        micros: microsOf({
          value: attachGroupValue({ group, key: NutritionAttachField.Micros }),
        }),
        dailyIntake: microsOf({
          value: attachGroupValue({
            group,
            key: NutritionAttachField.DailyIntake,
          }),
        }),
      },
    ];
  });
}

/**
 * One block item, read as a typed nutrition row — or null when it carries no
 * name, or names a record outside the pillar's own two. THE HEADLINE IS NOT
 * READ; `name:` is.
 */
export function readNutritionAttachItem(input: {
  item: AttachItemBlock;
}): NutritionAttachResult | null {
  const item = input.item;
  const name = fieldText({ item, key: NutritionAttachField.Name });
  if (name === null) return null;
  const kind = nutritionAttachKindOf({
    value: fieldText({ item, key: NutritionAttachField.Kind }),
  });
  if (kind === null) return null;
  return {
    name,
    kind,
    foodKind: fieldText({ item, key: NutritionAttachField.FoodKind }),
    brand: fieldText({ item, key: NutritionAttachField.Brand }),
    nutritionSummary: fieldText({ item, key: NutritionAttachField.Nutrition }),
    description: fieldText({ item, key: NutritionAttachField.Description }),
    numberOfServings: fieldNumber({ item, key: NutritionAttachField.Yields }),
    preparationTimeMinutes: fieldNumber({
      item,
      key: NutritionAttachField.PrepMinutes,
    }),
    cookingTimeMinutes: fieldNumber({
      item,
      key: NutritionAttachField.CookMinutes,
    }),
    gramsPerPortion: fieldNumber({
      item,
      key: NutritionAttachField.GramsPerPortion,
    }),
    types: fieldValues({ item, key: NutritionAttachField.Type }),
    categories: fieldValues({ item, key: NutritionAttachField.Category }),
    ingredients: fieldValues({ item, key: NutritionAttachField.Ingredient }),
    directions: fieldValues({ item, key: NutritionAttachField.Step }),
    photoUrls: fieldValues({ item, key: NutritionAttachField.Photo }),
    servings: servingsOf({ item }),
    linkUrl: fieldText({ item, key: NutritionAttachField.Link }),
  };
}
